/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { Form } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import {
  BinocularsIcon,
  RocketLaunchIcon,
  ShuffleIcon,
  TrendUpIcon,
} from 'bundles/onboarding-common/assets/LearningGoalIcons';
import GoalCard from 'bundles/onboarding-common/components/GoalCard';
import { ViewFormVisibilityTracker } from 'bundles/onboarding-common/components/ViewFormVisibilityTracker';
import {
  LEARNING_GOAL_OPTIONS,
  type LEARNING_GOAL_OPTIONS_TYPE,
  PATHWAYS_ONBOARDING_EVENT_NAME,
} from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { getLearningGoalOptionName } from 'bundles/onboarding-common/utils';
import {
  getLocalStorageOnboardingData,
  updateLocalStorageOnboardingData,
} from 'bundles/onboarding-common/utils/localStorageUtils';

import _t from 'i18n!nls/onboarding-common';

const styles = {
  container: (isCompact?: boolean) => css`
    display: flex;
    flex-direction: ${isCompact ? 'column' : 'row'};
    padding: var(--cds-spacing-300);
    justify-content: center;
    align-items: center;
    align-content: center;
    gap: var(--cds-spacing-200);
    border-radius: 24px;
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    background: var(--cds-color-white-0);

    ${breakpoints.down('md')} {
      flex-direction: column;
    }

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
      gap: var(--cds-spacing-300);
    }
  `,
  cardContainer: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);
    max-width: 1200px;

    ${breakpoints.down('sm')} {
      justify-content: center;
      width: 100%;
      flex-wrap: wrap;
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
      gap: var(--cds-spacing-100);
    }
  `,
};

type Props = {
  isCompact?: boolean;
  className?: string;
};

const InlineLearningGoalQuestion = ({ isCompact, className }: Props) => {
  const location = useLocation();
  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const [value, setValue] = useState<LEARNING_GOAL_OPTIONS_TYPE | null>(null);
  const { onboardingUrl, loading } = useBuildOnboardingUrl({
    questionId: QUESTION_IDS.roleSelect,
    referrer,
    learningGoal: value,
  });
  const track = useTracker();

  const formEventData: Form = {
    name: PATHWAYS_ONBOARDING_EVENT_NAME,
  };

  const handleClick = (val: string) => {
    track('click_button', {
      form: { ...formEventData, value: [val] },
      button: { name: 'inline_pathways_onboarding_learning_goal' },
    });

    const newValue = val as LEARNING_GOAL_OPTIONS_TYPE;
    setValue(newValue);

    const onboardingData = getLocalStorageOnboardingData();
    const previousLearningGoal = onboardingData?.[QUESTION_IDS.learningGoal]?.value;
    const shouldClearSavedSkills =
      previousLearningGoal === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS && previousLearningGoal !== newValue;
    const clearSavedSkills = shouldClearSavedSkills
      ? { [QUESTION_IDS.skillSelectAdvancer]: { __typename: QUESTION_IDS.skillSelectAdvancer, value: [] } }
      : {};

    updateLocalStorageOnboardingData({
      [QUESTION_IDS.learningGoal]: { __typename: QUESTION_IDS.learningGoal, value: newValue },
      ...clearSavedSkills,
    });
  };

  useEffect(() => {
    if (value && !loading) {
      setLocation(onboardingUrl);
    }
  }, [value, loading, onboardingUrl]);

  const learningGoalOptionNames = getLearningGoalOptionName();

  return (
    <ViewFormVisibilityTracker {...formEventData}>
      <div css={styles.container(isCompact)} className={className}>
        <Typography2 component="h2" variant="subtitleLarge">
          {_t('What brings you to Coursera today?')}
        </Typography2>
        <div css={styles.cardContainer}>
          <GoalCard
            value={LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB}
            description={learningGoalOptionNames.LEARNING_GOAL_LAND_NEW_JOB}
            onClick={handleClick}
            isCompact
            icon={<RocketLaunchIcon />}
            type="button"
            ariaLabel={_t('White rocket icon on blue background')}
          />
          <GoalCard
            value={LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER}
            description={learningGoalOptionNames.LEARNING_GOAL_CHANGE_CAREER}
            onClick={handleClick}
            isCompact
            icon={<ShuffleIcon />}
            type="button"
            ariaLabel={_t('White shuffle icon on blue background')}
          />
          <GoalCard
            value={LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS}
            description={learningGoalOptionNames.LEARNING_GOAL_IMPROVE_SKILLS}
            onClick={handleClick}
            isCompact
            icon={<TrendUpIcon />}
            type="button"
            ariaLabel={_t('White trend up icon on blue background')}
          />
          <GoalCard
            value={LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST}
            description={learningGoalOptionNames.LEARNING_GOAL_PERSONAL_INTEREST}
            onClick={handleClick}
            isCompact
            icon={<BinocularsIcon />}
            type="button"
            ariaLabel={_t('White binoculars icon on blue background')}
          />
        </div>
      </div>
    </ViewFormVisibilityTracker>
  );
};

export default InlineLearningGoalQuestion;
